import React, { useState, useEffect } from 'react';
import { type BoardingHouseInfo, type BoardingHouse } from '../types';

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (settings: { apiKey: string, updatedInfo: BoardingHouseInfo, houseIndex: number }) => void;
    currentApiKey: string;
    boardingHouses: BoardingHouse[];
    currentBoardingHouseIndex: number;
    onHouseChange: (index: number) => void;
    onHouseAdd: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ 
    isOpen, onClose, onSave, currentApiKey, boardingHouses, 
    currentBoardingHouseIndex, onHouseChange, onHouseAdd 
}) => {
    const [activeTab, setActiveTab] = useState('info');
    const [apiKey, setApiKey] = useState(currentApiKey);
    const [info, setInfo] = useState<BoardingHouseInfo | null>(null);

    useEffect(() => {
        setApiKey(currentApiKey);
        if (boardingHouses[currentBoardingHouseIndex]) {
            setInfo(boardingHouses[currentBoardingHouseIndex].info);
        }
    }, [currentApiKey, boardingHouses, currentBoardingHouseIndex, isOpen]);

    if (!isOpen || !info) return null;

    const handleInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;
        let finalValue: string | number = value;
        if (type === 'number') {
            finalValue = value === '' ? 0 : parseInt(value, 10);
            if (isNaN(finalValue) || finalValue < 0) finalValue = 0;
        }
        setInfo(prev => prev ? { ...prev, [name]: finalValue } : null);
    };

    const handleSave = () => {
        if (info) {
            onSave({ apiKey, updatedInfo: info, houseIndex: currentBoardingHouseIndex });
        }
        onClose();
    };

    const TabButton: React.FC<{ tabId: string; label: string }> = ({ tabId, label }) => (
        <li className="mr-2">
            <button
                onClick={() => setActiveTab(tabId)}
                className={`inline-block p-3 sm:p-4 border-b-2 rounded-t-lg text-sm sm:text-base ${
                    activeTab === tabId
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'
                }`}
            >
                {label}
            </button>
        </li>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[70] p-4 sm:p-0" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl h-full sm:h-auto sm:max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex-shrink-0 p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                        <h3 className="text-xl font-semibold text-gray-800 dark:text-white">Cài đặt</h3>
                    </div>
                    <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                        <select
                            value={currentBoardingHouseIndex}
                            onChange={(e) => onHouseChange(Number(e.target.value))}
                            className="input-field flex-grow"
                            aria-label="Chọn nhà trọ"
                        >
                            {boardingHouses.map((house, index) => (
                                <option key={house.id} value={index}>{house.info.name || `Nhà trọ ${index + 1}`}</option>
                            ))}
                        </select>
                        <button onClick={onHouseAdd} className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-500">
                            + Thêm nhà trọ
                        </button>
                    </div>
                </div>
                
                {/* Tabs */}
                <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-2 sm:px-4">
                    <ul className="flex -mb-px text-sm font-medium text-center">
                        <TabButton tabId="info" label="Thông tin nhà trọ" />
                        <TabButton tabId="api" label="Cài đặt API" />
                    </ul>
                </div>
                
                {/* Scrollable Content */}
                <div className="flex-grow overflow-y-auto p-4 sm:p-6">
                    {activeTab === 'info' && (
                        <div className="space-y-4">
                            <h4 className="font-semibold text-lg text-gray-800 dark:text-white">Thông tin cơ sở</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-field">Tên nhà trọ</label>
                                    <input name="name" value={info.name} onChange={handleInfoChange} className="input-field" placeholder="VD: Nhà trọ IVS StaySmart"/>
                                </div>
                                 <div>
                                    <label className="label-field">Số lượng phòng</label>
                                    <input type="number" name="numberOfRooms" value={info.numberOfRooms} onChange={handleInfoChange} className="input-field" min="1"/>
                                </div>
                            </div>
                            <div>
                                <label className="label-field">Địa chỉ (Số nhà, đường)</label>
                                <input name="address" value={info.address} onChange={handleInfoChange} className="input-field" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-field">Phường/Xã</label>
                                    <input name="ward" value={info.ward || ''} onChange={handleInfoChange} className="input-field"/>
                                </div>
                                <div>
                                    <label className="label-field">Tỉnh/Thành phố</label>
                                    <input name="city" value={info.city || ''} onChange={handleInfoChange} className="input-field"/>
                                </div>
                            </div>
                            <div>
                                <label className="label-field">Cơ quan Công an địa phương (để điền vào CT01)</label>
                                <input name="localPoliceDepartment" value={info.localPoliceDepartment || ''} onChange={handleInfoChange} className="input-field" placeholder="VD: Công an phường An Khánh, TP. Thủ Đức"/>
                            </div>
                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-field">Số điện thoại nhà trọ</label>
                                    <input name="phone" value={info.phone} onChange={handleInfoChange} className="input-field" />
                                </div>
                                 <div>
                                    <label className="label-field">Giá phòng mặc định (VNĐ)</label>
                                    <input type="number" name="defaultRoomPrice" value={info.defaultRoomPrice || 0} onChange={handleInfoChange} className="input-field" />
                                </div>
                            </div>
                            <div className="border-t border-gray-200 dark:border-gray-700 my-6"></div>
                            <h4 className="font-semibold text-lg text-gray-800 dark:text-white">Thông tin chủ sở hữu (Bên A)</h4>
                             <div>
                                <label className="label-field">Tên chủ nhà trọ</label>
                                <input name="landlordName" value={info.landlordName} onChange={handleInfoChange} className="input-field" />
                            </div>
                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-field">Ngày sinh chủ nhà</label>
                                    <input type="date" name="landlordDob" value={info.landlordDob || ''} onChange={handleInfoChange} className="input-field" />
                                </div>
                                <div>
                                    <label className="label-field">Số CCCD chủ nhà</label>
                                    <input name="landlordIdNumber" value={info.landlordIdNumber || ''} onChange={handleInfoChange} className="input-field" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-field">Ngày cấp CCCD</label>
                                    <input type="date" name="hostIdIssueDate" value={info.hostIdIssueDate || ''} onChange={handleInfoChange} className="input-field" />
                                </div>
                                <div>
                                    <label className="label-field">Nơi cấp CCCD</label>
                                    <input name="hostIdIssuePlace" value={info.hostIdIssuePlace || ''} onChange={handleInfoChange} className="input-field" placeholder="VD: Cúc Cánh sát quán lý hành chính vá trát tú xã hói" />
                                </div>
                            </div>
                            <div>
                                <label className="label-field">Địa chỉ thường trú chủ nhà</label>
                                <input name="hostPermanentAddress" value={info.hostPermanentAddress || ''} onChange={handleInfoChange} className="input-field" placeholder="Địa chỉ thường trú của chủ nhà trọ" />
                            </div>
                        </div>
                    )}
                    {activeTab === 'api' && (
                         <div className="space-y-4">
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Để sử dụng tính năng quét CCCD, bạn cần cung cấp Gemini API Key. Bạn có thể lấy key miễn phí từ Google AI Studio.
                            </p>
                            <div>
                                <label htmlFor="apiKey" className="label-field">
                                    Gemini API Key
                                </label>
                                <input
                                    type="password"
                                    id="apiKey"
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    className="input-field"
                                    placeholder="Nhập API Key của bạn tại đây"
                                />
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Footer */}
                <div className="flex-shrink-0 flex justify-end space-x-4 mt-auto p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
                    >
                        Hủy
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-500"
                    >
                        Lưu thay đổi
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SettingsModal;